import { Link } from 'react-router-dom';
import { Layout } from '@/components/layout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Helmet } from 'react-helmet';
import { Clock, ArrowRight, BookOpen } from 'lucide-react';
import { blogPosts } from '@/data/blogData';

const BlogPage = () => {
  return (
    <>
      <Helmet>
        <title>Blog - Supplement Guides &amp; Fitness Tips | NutraFuel Supplements</title>
        <meta name="description" content="Expert supplement guides, fitness tips, and nutrition advice for Indian athletes. Learn about whey protein, creatine, muscle gain, and beginner supplement stacks." />
        <meta name="keywords" content="supplement guide india, whey protein guide, creatine guide, muscle building supplements, fitness blog india" />
        <link rel="canonical" href="https://nutrafuel.in/blog" />
        <meta property="og:title" content="Blog - Supplement Guides & Fitness Tips | NutraFuel" />
        <meta property="og:description" content="Expert supplement guides and fitness tips for Indian athletes." />
        <meta property="og:url" content="https://nutrafuel.in/blog" />
      </Helmet>

      <Layout>
        {/* Hero */}
        <section className="relative pt-32 md:pt-40 pb-16 bg-gradient-to-b from-muted/50 to-background">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl">
              <div className="flex items-center gap-2 mb-4">
                <BookOpen className="w-5 h-5 text-primary" />
                <span className="text-primary font-semibold text-sm uppercase tracking-wider">Education &amp; Guides</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-foreground leading-tight mb-6" data-testid="blog-heading">
                Supplement <span className="text-primary">Knowledge Hub</span>
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Evidence-based guides, honest reviews, and practical advice to help you make smarter supplement choices. Written by athletes, for athletes.
              </p>
            </div>
          </div>
        </section>

        {/* Featured Post */}
        <section className="py-8 bg-background">
          <div className="container mx-auto px-4">
            <Link to={`/blog/${blogPosts[0].slug}`} data-testid="featured-blog-post">
              <Card className="group overflow-hidden border-0 shadow-soft hover:shadow-elevated transition-all duration-300">
                <div className="grid md:grid-cols-2">
                  <div className="aspect-video md:aspect-auto overflow-hidden bg-muted">
                    <img
                      src={blogPosts[0].image}
                      alt={blogPosts[0].title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <CardContent className="p-6 md:p-10 flex flex-col justify-center">
                    <div className="flex items-center gap-3 mb-4">
                      <Badge className="bg-primary/10 text-primary border-0">{blogPosts[0].category}</Badge>
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {blogPosts[0].readTime}
                      </span>
                    </div>
                    <h2 className="text-2xl md:text-3xl font-display font-bold text-foreground mb-3 group-hover:text-primary transition-colors">
                      {blogPosts[0].title}
                    </h2>
                    <p className="text-muted-foreground mb-6 line-clamp-3">{blogPosts[0].excerpt}</p>
                    <span className="inline-flex items-center gap-2 text-primary font-semibold text-sm">
                      Read Full Article
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </CardContent>
                </div>
              </Card>
            </Link>
          </div>
        </section>

        {/* All Posts Grid */}
        <section className="py-12 bg-background" data-testid="blog-posts-grid">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl font-display font-bold text-foreground mb-8">All Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {blogPosts.map((post) => (
                <Link key={post.id} to={`/blog/${post.slug}`} data-testid={`blog-card-${post.id}`}>
                  <Card className="group overflow-hidden border-0 shadow-soft hover:shadow-elevated transition-all duration-300 hover:-translate-y-1 h-full flex flex-col">
                    <div className="aspect-video overflow-hidden bg-muted">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <CardContent className="p-5 flex flex-col flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <Badge variant="secondary" className="text-xs">{post.category}</Badge>
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {post.readTime}
                        </span>
                      </div>
                      <h3 className="text-lg font-bold text-foreground mb-2 group-hover:text-primary transition-colors line-clamp-2">
                        {post.title}
                      </h3>
                      <p className="text-sm text-muted-foreground mb-4 flex-1 line-clamp-3">{post.excerpt}</p>
                      <div className="flex items-center justify-between mt-auto pt-3 border-t border-border">
                        <span className="text-xs text-muted-foreground">{post.author}</span>
                        <span className="text-xs text-muted-foreground">
                          {new Date(post.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* SEO Content */}
        <section className="py-12 bg-muted/20 border-t border-border">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-2xl font-display font-bold text-foreground mb-4">
                Your Trusted Source for Supplement Education
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                At NutraFuel, we believe informed athletes make better choices. Our blog provides honest, science-backed information about supplements, nutrition, and training to help you achieve your fitness goals safely and effectively. No hype, no fake claims &mdash; just the truth about what works.
              </p>
            </div>
          </div>
        </section>
      </Layout>
    </>
  );
};

export default BlogPage;
